import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function captureRefFromUrl() {
  try {
    const q = new URL(window.location.href).searchParams.get("ref");
    if (q) localStorage.setItem("sam_ref", q);
  } catch { /* noop */ }
}

export function readReferral() {
  const w = window;
  const ref = w.affiliateRef || localStorage.getItem("referly_affiliate_ref") || localStorage.getItem("sam_ref") || undefined;
  const click = (w.affiliateId != null ? String(w.affiliateId) : null) || localStorage.getItem("referly_click_id") || undefined;
  return { referral_ref: ref || undefined, referral_click: click || undefined };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingRole, setPendingRole] = useState("shopper");

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    captureRefFromUrl();
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const finishAuth = (data) => {
    setToken(data.token);
    setUser(data.user);
    setAuthOpen(false);
    toast.success(`Welcome, ${data.user.name.split(" ")[0]}!`);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    finishAuth(data);
  };
  const register = async (name, email, password, role) => {
    const { data } = await api.post("/auth/register", { name, email, password, role, ...readReferral() });
    finishAuth(data);
  };
  const logout = async () => {
    await api.post("/auth/logout");
    setToken(null);
    setUser(null);
    toast("Signed out");
  };

  const openAuth = (role = "shopper") => { setPendingRole(role); setAuthOpen(true); };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, openAuth, setUser, checkAuth }}>
      {children}
      <AuthModal open={authOpen} setOpen={setAuthOpen} defaultRole={pendingRole} />
    </AuthContext.Provider>
  );
}

function AuthModal({ open, setOpen, defaultRole }) {
  const { login, register } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: defaultRole });

  useEffect(() => { setForm((f) => ({ ...f, role: defaultRole })); }, [defaultRole]);

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (mode) => {
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password, form.role);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Join Saving America Money</DialogTitle>
          <DialogDescription>Log in or create a free account to save favorites, review businesses, and manage your listings.</DialogDescription>
        </DialogHeader>
        <Button variant="outline" className="w-full h-11 gap-2 border-2" onClick={google} data-testid="google-login-btn">
          <img alt="" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="h-5 w-5" />
          Continue with Google
        </Button>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
        </div>
        <Tabs defaultValue="login">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="login" data-testid="tab-login">Log In</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Sign Up</TabsTrigger>
          </TabsList>
          <TabsContent value="login" className="space-y-3 pt-3">
            <div><Label>Email</Label><Input type="email" data-testid="login-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Password</Label><Input type="password" data-testid="login-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <Button className="w-full bg-sam-navy hover:bg-sam-navy2" disabled={busy} onClick={() => submit("login")} data-testid="login-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Log In
            </Button>
          </TabsContent>
          <TabsContent value="register" className="space-y-3 pt-3">
            <div><Label>Full name</Label><Input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" data-testid="register-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Password</Label><Input type="password" data-testid="register-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="flex gap-2">
              {["shopper", "owner"].map((r) => (
                <button key={r} type="button" onClick={() => setForm({ ...form, role: r })}
                  className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-semibold capitalize transition ${form.role === r ? "border-sam-crimson bg-sam-crimson/5 text-sam-crimson" : "border-border text-muted-foreground"}`}
                  data-testid={`role-${r}`}>
                  {r === "shopper" ? "I'm a shopper" : "I own a business"}
                </button>
              ))}
            </div>
            <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" disabled={busy} onClick={() => submit("register")} data-testid="register-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create Account
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
